import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import './UsersTableComponent.css'; 

const fetchUsers = async (page, limit, sortField, sortOrder, fromDate, toDate, blocked) => {
  const { data } = await axios.get(`${process.env.REACT_APP_BASE_URL}/report/members-list`, {
    params: {
      page,
      limit,
      sortField,
      sortOrder,
      fromDate,
      toDate,
      blocked,
    },
  });
  return data;
};

const UsersTable = () => {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [blocked, setBlocked] = useState(null); // null: all, true: blocked, false: unblocked

  const itemsPerPage = 100;

  const { data, isLoading, error } = useQuery(
    ['users', page, sortField, sortOrder, fromDate, toDate, blocked],
    () =>
      fetchUsers(page, itemsPerPage, sortField, sortOrder, fromDate, toDate, blocked),
    {
      keepPreviousData: true,
    }
  );

  // Update page state only if necessary (prevents unnecessary re-renders)
  useEffect(() => {
    if (data && data.total) {
      const pageCount = Math.round(data.total / itemsPerPage) + ( (data.total / itemsPerPage) > 0 ? 1 : 0 )
      setTotalPages(pageCount);
    }
  }, [data, page]);

  const columns = [
    {
      Header: 'Phone',
      accessor: 'phoneNumber',
    },
    {
      Header: 'Created At',
      accessor: 'createdAt',
      Cell: ({ value }) => dayjs(value).format('DD-MMM-YYYY HH:mm'),
    },
    {
      Header: 'Blocked',
      accessor: 'isBlocked',
      Cell: ({ value }) => (value ? 'Yes' : 'No'),
    },
  ];

  const handleSort = (column) => {
    if (sortField === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(column);
      setSortOrder('asc');
    }
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error fetching data</div>;

  return (
    <div className="table-container">
      {/* Filter Section */}
      <div className="filter-section">
        <div className="filter-group">
          <label htmlFor="fromDate" className="filter-label">From Date:</label>
          <input
            id="fromDate"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="filter-input"
            aria-label="From Date"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="toDate" className="filter-label">To Date:</label>
          <input
            id="toDate"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="filter-input"
            aria-label="To Date"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="blocked" className="filter-label">Blocked Status:</label>
          <select
            id="blocked"
            value={blocked}
            onChange={(e) => setBlocked(e.target.value)}
            className="filter-input"
            aria-label="Blocked Status"
          >
            <option value="">All</option>
            <option value="true">Blocked</option>
            <option value="false">Unblocked</option>
          </select>
        </div>
      </div>

      <p>Showing {itemsPerPage} users per page</p>

      <table className="users-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.Header}
                onClick={() => handleSort(column.accessor)}
                className={`sortable ${sortField === column.accessor ? 'sorted' : ''}`}
              >
                {column.Header}
                {/* {sortField === column.accessor && (sortOrder === 'asc' ? ' 🔼' : ' 🔽')} */}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.users.map((user) => (
            <tr key={user._id}>
              <td>{user.phoneNumber}</td>
              <td>{dayjs(user.createdAt).format('DD-MMM-YYYY HH:mm')}</td>
              <td>{user.isBlocked ? 'Yes' : 'No'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div className="pagination" role='navigation' aria-label="Pagination Controls">
        <button onClick={() => setPage(1)} disabled={page === 1} className="page-button"
          aria-label="Go to first page">
          {'<<'}
        </button>
        <button onClick={() => setPage(page - 1)} disabled={page === 1} className="page-button"
          aria-label="Go to previous page">
          {'<'}
        </button>
        <button onClick={() => setPage(page + 1)} disabled={page === data.pageCount} className="page-button"
          aria-label="Go to next page">
          {'>'}
        </button>
        <button onClick={() => setPage(data.pageCount)} disabled={page === data.pageCount} className="page-button"
          aria-label="Go to last page">
          {'>>'}
        </button>
        <span className="page-info">
          Page {page} of {totalPages}
        </span>
      </div>
    </div>
  );
};

export default UsersTable;
